import React, { useCallback, useEffect, useState } from 'react';
import usePagination from '../../../components/Pagination/usePagination';
import Pagination from '../../../components/Pagination/Pagination';
import { Table } from '../../../components/Table';
import Modal from '../../../components/Modal';
import { useIotDevicesStore } from './use-iot-devices-store';
import EmailIcon from '@mui/icons-material/Email';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import MemoryIcon from '@mui/icons-material/Memory';
import dayjs from 'dayjs';
import { SIM_STATUS_CONFIG, SIM_STATUSES_OPTIONS } from './constants';
import SearchBar from '../../../components/SearchBar/SearchBar';

const LIMIT = 10;
const INITIAL_VALUES = { serial_number: '', imsi: '', status: '' };

const IotDevicesModal = () => {
  const { setShowModal, showModal, data, total, error, fetchIotDevices } = useIotDevicesStore();
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT });
  const totalPages = Math.ceil(total / limit);
  const [filterValue, setFilterValue] = useState(INITIAL_VALUES);

  // sort:
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const handleSort = useCallback(
    (newSortBy, newSortOrder) => {
      setSortBy(newSortBy || '');
      setSortOrder(newSortOrder || 'asc');
      updatePageUrl(1);
    },
    [updatePageUrl]
  );

  // filters:
  const handleSearch = useCallback(
    (value, name) => {
      setFilterValue((prev) => ({ ...prev, [name]: value }));
      updatePageUrl(1);
    },
    [updatePageUrl]
  );

  const inputsFilters = [
    {
      type: 'simpleSearch',
      placeholder: 'По серийному номеру',
      defaultValue: filterValue?.serial_number,
      handleSearch: (value) => handleSearch(value, 'serial_number'),
      prefix: <MemoryIcon fontSize={'small'} className={'text-gray-500'} />,
    },
    {
      type: 'simpleSearch',
      placeholder: 'По ID SIM-карты',
      defaultValue: filterValue?.imsi,
      handleSearch: (value) => handleSearch(value, 'imsi'),
      prefix: <EmailIcon fontSize={'small'} className={'text-gray-500'} />,
    },
    {
      type: 'selector',
      placeholder: 'По статусу',
      options: SIM_STATUSES_OPTIONS,
      selectedOptions: filterValue.status,
      onChange: (value) => handleSearch(value, 'status'),
      prefix: <FilterAltOutlinedIcon fontSize="small" className="text-gray-500 mr-2" />,
    },
  ];

  useEffect(() => {
    fetchIotDevices(page, limit, filterValue, sortBy, sortOrder);
  }, [page, limit, filterValue, sortBy, sortOrder]);

  const columns = [
    {
      key: 'user_id',
      title: 'ID польз.',
      render: (value) => {
        return <div>{value ?? 'SIM не привязана'}</div>;
      },
    },
    {
      key: 'serial_number',
      title: 'Серийный номер',
      sortable: true,
      render: (value) => {
        return <div className={'min-w-[120px]'}>{value ?? '-'}</div>;
      },
    },
    {
      key: 'data_value_kb',
      title: 'Текущий остаток пакета, кб',
      sortable: true,
    },
    {
      key: 'imsi',
      title: 'ID SIM-карты',
      sortable: true,
    },
    {
      key: 'status',
      title: 'Статус',
      sortable: true,
      render: (value) => {
        const status = SIM_STATUS_CONFIG[value];

        return (
          <div className="flex items-center gap-1 min-w-[150px]">
            {status ? (
              <>
                {status.icon}
                {status.label}
              </>
            ) : (
              '-'
            )}
          </div>
        );
      },
    },
    {
      key: 'activated_at',
      title: 'Дата активации',
      sortable: true,
      render: (value) => {
        return <div>{value ? dayjs(value).format('DD.MM.YYYY') : '-'}</div>;
      },
    },
    {
      key: 'package_expires_at',
      title: 'Окончание пакета',
      sortable: true,
      render: (value) => {
        return <div>{value ? dayjs(value).format('DD.MM.YYYY') : '-'}</div>;
      },
    },
  ];

  return (
    <Modal
      classWidth={'relative w-11/12 lg:max-w-[1000px] my-6 mx-2'}
      showModal={showModal}
      setShowModal={setShowModal}
      title="Окончание пакета NB-IoT"
      showSave={false}
      error={null}>
      <div>
        <SearchBar inputs={inputsFilters} />
        <Table columns={columns} data={data} classContainer={'shadow-none mt-2'} sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
          {error || totalPages === 0 ? null : <Pagination initialLimit={LIMIT} totalPages={totalPages} withLimit={true} />}
        </Table>
      </div>
    </Modal>
  );
};

export default IotDevicesModal;
